import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';

export interface TotalClosingCostsData {
  closingCostsSubtotal: number;
  lenderCredits: number;
}

export interface CDTotalClosingCostsProps {
  /** Total closing costs data */
  data: Partial<TotalClosingCostsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<TotalClosingCostsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDTotalClosingCosts - Section J: Total Closing Costs on Page 2
 *
 * Shows the final total of all closing costs (D + I) and Lender Credits.
 */
export const CDTotalClosingCosts = React.forwardRef<HTMLDivElement, CDTotalClosingCostsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const updateField = (field: keyof TotalClosingCostsData, value: number) => {
      onChange?.({ ...data, [field]: value });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <div className="cd-section-header">J. TOTAL CLOSING COSTS (Borrower-Paid)</div>

        <div className="border border-t-0 border-cd-black">
          {/* Closing Costs Subtotals row */}
          <div className="grid grid-cols-[1fr_120px] border-b border-cd-black">
            <div className="px-2 py-1 cd-text-small font-bold">Closing Costs Subtotals (D + I)</div>
            <div className="px-2 py-1 text-right border-l border-cd-black">
              <CDCurrency
                value={data.closingCostsSubtotal}
                editable={editable}
                onChange={(v) => updateField('closingCostsSubtotal', v)}
                className="text-[12pt] font-bold"
              />
            </div>
          </div>

          {/* Lender Credits row */}
          <div className="grid grid-cols-[1fr_120px]">
            <div className="px-2 py-1 cd-text-small font-bold">Lender Credits</div>
            <div className="px-2 py-1 text-right border-l border-cd-black">
              {editable ? (
                <CDCurrency
                  value={data.lenderCredits}
                  editable={true}
                  onChange={(v) => updateField('lenderCredits', v)}
                  className="cd-text-small"
                />
              ) : data.lenderCredits && data.lenderCredits > 0 ? (
                <CDCurrency
                  value={-data.lenderCredits}
                  useParentheses={true}
                  className="cd-text-small"
                />
              ) : (
                <span className="cd-text-small">&mdash;</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDTotalClosingCosts.displayName = 'CDTotalClosingCosts';
